/**
 * AVBP Project Ledger + money-before-delivery gates + template versioning
 * — Phase 1 · Sprint 4.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  Sprint 2 created an append-only `projects/{pid}/ledger` that recorded
 *  ONLY review-commit events. Sprint 4 makes that ledger the canonical
 *  IMMUTABLE per-project record of every consequential project event and
 *  adds the two things that hang off it:
 *
 *    A. Project Ledger + auto-recall
 *       - Every consequential event (recommendation · decision · gate
 *         clearance/payment · template-version lock · stage transition)
 *         appends one immutable ledger entry. Never mutated/deleted —
 *         verbatim-sacrosanct discipline (memory
 *         feedback_client_verbatim_preservation).
 *       - Auto-recall (memory feedback_avbp_recommendation_memory): every
 *         per-project recommendation carries a deterministic topicKey.
 *         When the same topic re-arises the system surfaces the prior
 *         recommendations so long engagements (L2C 24mo, L3B) stay
 *         consistent. The topicKey derivation is deterministic; an
 *         LLM-similarity refinement is a single typed seam (mirrors the
 *         Sprint-2 extractor seam) — swap `activeTopicMatcher`, nothing
 *         else changes.
 *
 *    B. money-before-delivery gates — REAL now
 *       - Gate state lives on the project doc (`gateState`) and EVERY
 *         clearance is also an immutable ledger append (a clearance is an
 *         event). `canEnterStage` (engagementTemplate, Sprint 3) already
 *         computes the verdict from a GateState; Sprint 4 makes that
 *         GateState real per-project persisted data and makes a blocked
 *         stage genuinely unenterable (`assertStageEntry`). Server recheck
 *         where a gate guards delivery: see SERVER-RECHECK below.
 *       - Per-tier amounts/splits are NOT re-derived here — they come from
 *         the LOCKED engagementTemplate gate config (which traces to the
 *         FULLSCOPE/LADDER docs). This module only tracks WHICH gates are
 *         cleared, never invents an amount.
 *
 *    C. Template versioning (memory project_avbp_iterative_streamline_strategy,
 *       DB task #876)
 *       - A project locks the engagementTemplate version it was created
 *         under. Streamline edits apply only to NEW engagements; in-flight
 *         projects keep their original version. The version is stamped on
 *         the project doc + as a ledger entry at creation, and the
 *         Project/Conversation shell resolves the tier model by the
 *         project's locked version (sane fallback to current for
 *         legacy/demo projects).
 *
 *  This module is split: PURE logic (topicKey, recall matching, version
 *  resolution, gate-state reducers) has NO Firebase import so the Node
 *  native test runner exercises it directly via _tsload.cjs — exactly the
 *  Sprint-3 discipline. The Firestore read/write helpers live in
 *  conversation.ts (which already owns the collection paths + listeners).
 * ─────────────────────────────────────────────────────────────────────────
 */
import {
  assertServerSideGate,
  canEnterStage,
  type EngagementTemplate,
  type Gate,
  type GateState,
  type PaymentModePolicy,
  type ServerGateRecheck,
} from './engagementTemplate';

/* ════════════════════ Ledger event model ════════════════════ */

/**
 * Every consequential project event. Sprint 2 only had the implicit
 * "commit" event (kind mirrored the proposal kind). Sprint 4 makes the
 * event TYPE explicit and append-only across all event classes.
 */
export type LedgerEventType =
  | 'proposal_commit' //  Sprint-2 review-queue commit (back-compat)
  | 'recommendation' //   a recommendation made on the project (recall source)
  | 'decision' //         an owner/staff decision recorded
  | 'gate_clearance' //   a money / consultation / curation gate cleared
  | 'gate_override' //    admin override of a money gate (Phase 1-C, SC-058)
  | 'template_version_lock' // project locked its engagementTemplate version
  | 'stage_transition' //  project entered a new stage
  // ── spec-reconciliation 2026-05-21 (audit punch-list blocker 3) ──
  // Report delivery (Phase 2) needs these two. Spec v1.1.0
  // projectLedger.entryTypes defines `deliverable_event` +
  // `version_event`; the engine was missing both.
  | 'deliverable_event' // an artifact released (quote/report/drawing/kit)
  | 'version_event'; //   a report/drawing version bump (supersedes a prior)

/**
 * One immutable ledger entry. Superset of the Sprint-2 shape — the
 * original review-commit fields stay so existing rows + listeners keep
 * working; the new fields are optional and event-type specific.
 *
 * APPEND-ONLY: there is deliberately no update/delete export anywhere.
 */
export interface LedgerEventBase {
  id: string;
  /** Sprint-4 discriminator. Absent on legacy Sprint-2 rows → treated as
   *  'proposal_commit' by `eventTypeOf` for back-compat. */
  eventType?: LedgerEventType;
  /** Human-readable one-liner shown in the ledger / recall surface. */
  summary: string;
  actorUid: string;
  actorName: string;
  /** Firestore serverTimestamp; null until it resolves client-side. */
  at: unknown;
}

export interface RecommendationEvent extends LedgerEventBase {
  eventType: 'recommendation';
  /**
   * Deterministic recall key (zone/concern/topic). The auto-recall
   * read-path groups by this. Stable for the same concern so a re-ask
   * surfaces the prior recommendation.
   */
  topicKey: string;
  /** What was recommended (verbatim-ish, for the recall card body). */
  recommendation: string;
  /** Optional FK back to the verbatim message that prompted it. */
  sourceMessageId?: string;
}

export interface GateClearanceEvent extends LedgerEventBase {
  eventType: 'gate_clearance';
  gateId: string;
  gateKind: Gate['kind'];
  /** The stage this gate was blocking — for the audit trail. */
  blocksStageKey: string;
}

export interface TemplateVersionLockEvent extends LedgerEventBase {
  eventType: 'template_version_lock';
  tier: string;
  templateVersion: string;
}

export interface StageTransitionEvent extends LedgerEventBase {
  eventType: 'stage_transition';
  fromStageKey: string | null;
  toStageKey: string;
}

export type LedgerEvent =
  | LedgerEventBase
  | RecommendationEvent
  | GateClearanceEvent
  | TemplateVersionLockEvent
  | StageTransitionEvent;

/** Legacy Sprint-2 rows have no eventType → they are commits. */
export function eventTypeOf(e: { eventType?: LedgerEventType }): LedgerEventType {
  return e.eventType ?? 'proposal_commit';
}

/* ════════════════════ A. Auto-recall ════════════════════ */

/**
 * Deterministic topic-key derivation. Same concern → same key, always,
 * so a re-ask about "the SE toilet" recalls the earlier recommendation.
 *
 * Strategy (legible, dependency-free): canonicalise to a small set of
 * known Vastu concern facets — direction × subject — falling back to a
 * stable slug of the salient nouns. This is the deterministic baseline;
 * the LLM-similarity refinement is the `TopicMatcher` seam below.
 */
const DIRECTIONS = [
  'north-east',
  'south-east',
  'south-west',
  'north-west',
  'north',
  'south',
  'east',
  'west',
  'ne',
  'se',
  'sw',
  'nw',
  'brahmasthan',
  'centre',
  'center',
];

const SUBJECTS = [
  'toilet',
  'septic',
  'kitchen',
  'entrance',
  'door',
  'staircase',
  'stairs',
  'bedroom',
  'water',
  'borewell',
  'tank',
  'pooja',
  'mandir',
  'cash',
  'safe',
  'overhead',
  'underground',
  'slope',
  'extension',
  'cut',
];

const DIR_CANON: Record<string, string> = {
  ne: 'north-east',
  se: 'south-east',
  sw: 'south-west',
  nw: 'north-west',
  center: 'centre',
  brahmasthan: 'centre',
};

/** Pure: text → deterministic topic key (or '' when nothing salient). */
export function deriveTopicKey(text: string): string {
  const t = ` ${text.toLowerCase().replace(/[^a-z0-9]+/g, ' ')} `;
  let dir = '';
  for (const d of DIRECTIONS) {
    if (t.includes(` ${d} `)) {
      dir = DIR_CANON[d] ?? d;
      break;
    }
  }
  let subj = '';
  for (const s of SUBJECTS) {
    if (t.includes(` ${s} `) || t.includes(` ${s}s `)) {
      subj = s;
      break;
    }
  }
  if (subj && dir) return `${subj}@${dir}`;
  if (subj) return subj;
  if (dir) return `zone@${dir}`;
  // Fallback: first two salient words, stable + slugged.
  const words = t
    .trim()
    .split(/\s+/)
    .filter((w) => w.length > 3);
  return words.slice(0, 2).join('-');
}

/**
 * TopicMatcher SEAM — mirrors the Sprint-2 extractor seam exactly.
 * The deterministic baseline is `keyMatcher`. In production an
 * LLM-similarity matcher can decide that "the loo in the back corner"
 * and "toilet@south-west" are the same concern. Swap `activeTopicMatcher`
 * — nothing else changes. Kept a single, marked insertion point.
 */
export interface TopicMatcher {
  /** Does a new concern text match an existing recorded topicKey? */
  matches: (newText: string, existingTopicKey: string) => boolean;
  /** Derive the canonical key to STORE for a recommendation. */
  keyFor: (text: string) => string;
}

export const keyMatcher: TopicMatcher = {
  matches: (newText, existingTopicKey) =>
    deriveTopicKey(newText) === existingTopicKey,
  keyFor: (text) => deriveTopicKey(text),
};

/** Swap THIS for the LLM-similarity matcher. Keep the seam single. */
export const activeTopicMatcher: TopicMatcher = keyMatcher;

/**
 * Auto-recall read-path. Given the recommendation events on a project and
 * the concern currently being raised, return the prior recommendations on
 * the SAME topic, newest first. Empty array = nothing to recall (so the
 * surface simply doesn't render). Pure — caller supplies the events it
 * already has from the realtime listener.
 */
export function recallPriorRecommendations(
  events: RecommendationEvent[],
  concernText: string,
  matcher: TopicMatcher = activeTopicMatcher,
): RecommendationEvent[] {
  if (!concernText.trim()) return [];
  const hits = events.filter((e) =>
    matcher.matches(concernText, e.topicKey),
  );
  // Newest first. `at` may be null pre-resolve → fall back to insertion
  // order being chronological (listener orders by `at` desc already, so a
  // stable reverse keeps newest-first even before timestamps resolve).
  return [...hits].reverse();
}

/** Group every recommendation event by topicKey (ledger "by concern"
 *  view). Deterministic ordering by key for a stable UI. */
export function groupRecommendationsByTopic(
  events: RecommendationEvent[],
): Array<{ topicKey: string; items: RecommendationEvent[] }> {
  const m = new Map<string, RecommendationEvent[]>();
  for (const e of events) {
    const arr = m.get(e.topicKey) ?? [];
    arr.push(e);
    m.set(e.topicKey, arr);
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([topicKey, items]) => ({ topicKey, items }));
}

/* ════════════════════ B. Real money-before-delivery gates ════════════════════ */

/**
 * Persisted per-project gate state shape. This is what lives on the
 * project doc under `gateState` and is the SOURCE for the Sprint-3
 * `canEnterStage` verdict (which already takes a GateState).
 *
 * It is the exact `GateState` from engagementTemplate — re-exported so
 * callers have one import. Sprint 3 left it computed-from-nothing; Sprint
 * 4 persists it and every transition is also a ledger append.
 */
export type ProjectGateState = GateState;

export function emptyGateState(): ProjectGateState {
  return {
    moneyPaid: {},
    curationCleared: false,
    consultationDone: {},
    qaSignedOff: {},
  };
}

/**
 * Pure reducer: apply a single gate clearance to a gate-state object,
 * returning a NEW object (never mutates — the persisted doc is replaced,
 * the ledger append is the immutable history). The caller persists the
 * result + appends the GateClearanceEvent atomically (see conversation.ts
 * `clearGate`).
 */
export function applyGateClearance(
  prev: ProjectGateState,
  gate: Gate,
): ProjectGateState {
  if (gate.kind === 'money') {
    return {
      ...prev,
      moneyPaid: { ...prev.moneyPaid, [gate.id]: true },
    };
  }
  if (gate.kind === 'curation') {
    return { ...prev, curationCleared: true };
  }
  if (gate.kind === 'qa') {
    // Ancillary-tier QA sign-off (Rohit verified north + scale/zones).
    return {
      ...prev,
      qaSignedOff: { ...(prev.qaSignedOff ?? {}), [gate.id]: true },
    };
  }
  return {
    ...prev,
    consultationDone: { ...prev.consultationDone, [gate.id]: true },
  };
}

/**
 * THE real client-side enforcement gate. A blocked stage is genuinely
 * unenterable: this throws `StageBlockedError` (carrying the blocking
 * gates) when the persisted gate state does not clear `stageKey`. The
 * Sprint-3 `canEnterStage` is reused verbatim for the verdict — Sprint 4
 * does not re-implement gate logic, it ENFORCES it against real state.
 *
 * ── SERVER-RECHECK (mandatory where a gate guards delivery) ───────────
 * For gates that guard actual DELIVERY (every `curation` and `qa` quality
 * gate, plus the final/terminal money gate), the authoritative re-check
 * MUST run server-side. That recheck is the explicit typed seam
 * `assertServerSideGate` in engagementTemplate.ts (Phase 1 Task B made it
 * real) — `assertStageEntry` routes delivery-guarding stages through it
 * so the server implementation sits on the real path.
 *
 * ── SC-037 (owner ruling 2026-05-21, RELAXED) ─────────────────────────
 * The server recheck verifies the project's MONEY + QUALITY state — it
 * MUST NOT 403 by assignee. Report curation is no longer gated to the
 * project `assignee_uid`: assignment is a soft responsibility tag (who is
 * accountable), and *any* staff member may generate a final report for
 * *any* client, assigned or not. (Relaxes the earlier
 * project_curation_gating_assignee_model lock; spec scenario SC-037,
 * "approved 2026-05-21 — assignment is a responsibility, NOT a
 * restriction gate"; to be revisited later.) The curation gate REMAINS
 * as a delivery-guarding QUALITY checkpoint — it only flags the stage as
 * delivery-guarding so the server-recheck seam runs; it does not block by
 * caller identity. See the report for the exact server contract.
 */
export class StageBlockedError extends Error {
  blockedBy: Gate[];
  constructor(stageKey: string, blockedBy: Gate[]) {
    super(
      `Stage "${stageKey}" is gated — ${blockedBy
        .map((g) => ('label' in g ? g.label : g.kind))
        .join(' · ')}`,
    );
    this.name = 'StageBlockedError';
    this.blockedBy = blockedBy;
  }
}

/**
 * Does any gate blocking `stageKey` guard actual delivery? A `curation`
 * (core-tier report quality checkpoint) or `qa` (ancillary-tier Rohit
 * sign-off) gate always does — both are delivery-guarding quality gates.
 * A money gate that blocks a terminal/delivery stage does too;
 * conservatively, any gate whose blocked stage has no later stage (the
 * engagement's last stage) is delivery-guarding.
 */
export function isDeliveryGuarded(
  tpl: EngagementTemplate,
  stageKey: string,
): boolean {
  const gates = tpl.gates.filter((g) => g.blocksStageKey === stageKey);
  if (gates.some((g) => g.kind === 'curation' || g.kind === 'qa')) return true;
  const idx = tpl.stages.findIndex((s) => s.key === stageKey);
  const isTerminal = idx >= 0 && idx === tpl.stages.length - 1;
  return isTerminal && gates.length > 0;
}

/**
 * Enforced stage entry. Throws StageBlockedError if blocked. For
 * delivery-guarding stages the verdict is routed through
 * `assertServerSideGate` (engagementTemplate.ts) — the single
 * server-enforcement seam.
 *
 * Phase 1 Task B (2026-05-21): `assertServerSideGate` is no longer a
 * no-op. For a delivery-guarding stage:
 *   - serverRecheck SUPPLIED → the server's verdict is authoritative.
 *   - serverRecheck MISSING  → FAIL-SAFE block (a delivery write must not
 *     proceed without server authority). Previously a missing recheck
 *     silently passed the client verdict — that gap is closed.
 * Non-delivery-guarding (plain work) stages are unaffected — they never
 * reach the server seam, so the client verdict stands.
 *
 * Returns silently when allowed.
 */
export async function assertStageEntry(
  tpl: EngagementTemplate,
  stageKey: string,
  state: ProjectGateState,
  serverRecheck?: ServerGateRecheck,
  // owner directive 2026-05-19: configurable money policy. Default keeps
  // the locked money-before-delivery hard block for every existing caller.
  // 'work_first' → money gates don't block here either (mirrors
  // canEnterStage); curation/consultation gates still block.
  paymentMode: PaymentModePolicy = 'payment_before',
): Promise<void> {
  const verdict = canEnterStage(tpl, stageKey, state, paymentMode);
  let final = verdict;
  if (isDeliveryGuarded(tpl, stageKey)) {
    // Delivery-guarding stage → MUST clear the server-side seam.
    // assertServerSideGate fail-safes to a block when no recheck is
    // supplied, so a delivery write can never slip through unverified.
    final = await assertServerSideGate(tpl, stageKey, verdict, serverRecheck);
  }
  if (!final.allowed) {
    throw new StageBlockedError(stageKey, final.blockedBy);
  }
}

/* ════════════════════ C. Template versioning ════════════════════ */

/**
 * The engagementTemplate version. Streamline edits to a tier's template
 * BUMP this constant; in-flight projects keep the version they were
 * created under (stamped on the project doc + a ledger entry at creation)
 * so the workflow never shifts under staff/clients mid-engagement.
 *
 * `v1.2026-05-21` — spec-conformance reconciliation pass against
 * avbp-spec.json v1.1.0 (audit AVBP_ENGINE_SPEC_AUDIT_2026-05-21):
 *  - 3 ancillary tiers added (SiteVisit / Survey & Map Making / Map
 *    Grading);
 *  - L1A Sanskrit Seal corrected + stage_20 post-delivery follow-up;
 *  - L2A/L2C/L2D/L3A/L3B display names reverted to spec name.modern;
 *  - L2C Devta surface re-bound to P4 (spec Phase 4);
 *  - machine-readable `revisionsCap` added to design tiers.
 * Prior: `v1.2026-05-17` (pre-Gate-1; kept registered so any project
 * locked to it still resolves `exact`).
 */
export const ENGAGEMENT_TEMPLATE_VERSION = 'v1.2026-05-21';

/**
 * Known historical template versions, newest last. The registry lets the
 * resolver give a defined `exact` verdict for a project locked to ANY
 * known version. `TEMPLATES` today is the CURRENT version
 * (`ENGAGEMENT_TEMPLATE_VERSION`); a future streamline sprint that
 * needs per-version frozen config keys it here. The prior pre-Gate-1
 * version stays registered so a project created under it is still
 * resolved exact (its config did shift in the reconciliation, but no
 * real in-flight project predates the rebuild — kept for honesty).
 */
export const KNOWN_TEMPLATE_VERSIONS = [
  'v1.2026-05-17',
  ENGAGEMENT_TEMPLATE_VERSION,
] as const;

export function isKnownTemplateVersion(v: string | null | undefined): boolean {
  return !!v && (KNOWN_TEMPLATE_VERSIONS as readonly string[]).includes(v);
}

/**
 * Resolve the template version a project's shell must render. A project
 * carries its locked `templateVersion`. Legacy/demo projects created
 * before versioning have none → fall back to current (sane, stated). An
 * unknown future-looking version also falls back to current with a flag
 * so the UI can note "rendering with current template (locked version
 * unavailable)".
 */
export interface ResolvedTemplateVersion {
  version: string;
  /** false → the project's locked version was missing/unknown and we
   *  fell back to current. UI surfaces this honestly. */
  exact: boolean;
}

export function resolveTemplateVersion(
  projectVersion: string | null | undefined,
): ResolvedTemplateVersion {
  if (isKnownTemplateVersion(projectVersion)) {
    return { version: projectVersion as string, exact: true };
  }
  return { version: ENGAGEMENT_TEMPLATE_VERSION, exact: false };
}
